// lockbox-web.ts — 1:1 JS port of the THREE on-device Rust engines (fleet zero-stub sweep
// 2026-08-20). Where this and the Rust could drift, the Rust wins; the Rust files' own
// #[test]s are mirrored in the unit fixture.
//
//   ledger.rs  → deriveKeyDates()      — deterministic key-date extraction at import time
//   signals.rs → deriveHeadlineFacts() — the AI-MAX pre-ranked fact sentences
//   db.rs      → chunkText()/ftsTokens()/searchPassages() — the local retrieval step
//
// PRIVACY SPINE UNCHANGED: whole documents never leave the device. Search picks passages,
// facts are short derived sentences; only those ride the ONE billed call. Everything here
// is deterministic — it only LOCATES and restates what the document says; never advice.
// (No FTS5 in the browser: searchPassages chunks bodies on the fly and scores by token
// prefix-matches — AND pass first for precision, then OR for recall, like the Rust.)

// ── shared helpers ──────────────────────────────────────────────────────────────
const isDigit = (c: string) => c >= "0" && c <= "9";
const isAlpha = (c: string) => /[a-zA-Z]/.test(c);
const validIso = (y: number, m: number, d: number): string | null => {
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

// ── ledger.rs port: key-date extraction ────────────────────────────────────────
const RENEW_CUES = ["renew", "renewal", "renews", "auto-renew", "rollover"];
const EXPIRE_CUES = ["expire", "expires", "expiry", "expiration", "valid until", "valid till", "ends on", "end date", "coverage until", "covered until", "until"];
const NOTICE_CUES = ["notice", "notify", "cancel by", "cancellation", "terminate by"];
const DUE_CUES = ["due", "payable", "payment due", "pay by", "deadline"];
// Commencement dates had no label of their own, so they fell through to "due" at best and were
// captured by a neighbouring clause's "expires" at worst. A start date is a fact worth stating.
const START_CUES = ["commence", "commences", "commencing", "begins", "beginning", "starts", "start date", "effective from", "effective on", "from and including", "term begins"];

const MONTHS: [string, number][] = [
  ["january", 1], ["jan", 1], ["february", 2], ["feb", 2], ["march", 3], ["mar", 3],
  ["april", 4], ["apr", 4], ["may", 5], ["june", 6], ["jun", 6], ["july", 7], ["jul", 7],
  ["august", 8], ["aug", 8], ["september", 9], ["sept", 9], ["sep", 9],
  ["october", 10], ["oct", 10], ["november", 11], ["nov", 11], ["december", 12], ["dec", 12],
];
const monthNum = (name: string): number | null => {
  const n = name.trim().toLowerCase();
  const hit = MONTHS.find(([m]) => m === n);
  return hit ? hit[1] : null;
};

interface DateHit { iso: string; start: number; end: number }

/** "2026-09-01" (also "2026/09/01"); not followed by another digit. */
function tryIso(text: string, at: number): [string, number] | null {
  const s = text.slice(at);
  if (s.length < 10) return null;
  const sep1 = s[4];
  if (sep1 !== "-" && sep1 !== "/") return null;
  if (!(/^\d{4}$/.test(s.slice(0, 4)) && isDigit(s[5]) && isDigit(s[6]) && (s[7] === "-" || s[7] === "/") && isDigit(s[8]) && isDigit(s[9]))) return null;
  if (s.length > 10 && isDigit(s[10])) return null;
  const iso = validIso(Number(s.slice(0, 4)), Number(s.slice(5, 7)), Number(s.slice(8, 10)));
  return iso ? [iso, 10] : null;
}

/** "01/09/2026" or "1.9.26" — DAY-FIRST. */
function tryDmySlashes(text: string, at: number): [string, number] | null {
  const s = text.slice(at);
  const parts: string[] = [];
  let cur = "", consumed = 0;
  for (const ch of s) {
    if (isDigit(ch)) {
      cur += ch;
      consumed += 1;
      if (cur.length > 4) return null;
    } else if (ch === "/" || ch === ".") {
      if (cur === "") return null;
      parts.push(cur);
      cur = "";
      consumed += 1;
      if (parts.length > 2) return null;
    } else break;
  }
  if (cur !== "") parts.push(cur);
  if (parts.length !== 3) return null;
  const d = Number(parts[0]), m = Number(parts[1]);
  let y = Number(parts[2]);
  if (parts[2].length === 2) y += 2000;
  if (parts[2].length !== 4 && parts[2].length !== 2) return null;
  const iso = validIso(y, m, d);
  return iso ? [iso, consumed] : null;
}

/** "1 September 2026" / "1st Sep 2026". */
function tryDayMonthYear(text: string, at: number): [string, number] | null {
  const s = text.slice(at);
  let dayStr = "";
  for (const ch of s) { if (isDigit(ch)) dayStr += ch; else break; }
  if (dayStr === "" || dayStr.length > 2) return null;
  let idxAfterDay = dayStr.length;
  const suffix = s.slice(idxAfterDay, idxAfterDay + 2).toLowerCase();
  if (["st", "nd", "rd", "th"].includes(suffix)) idxAfterDay += 2;
  const afterDay = s.slice(idxAfterDay);
  const trimmed = afterDay.replace(/^\s+/, "");
  const leadingWs = afterDay.length - trimmed.length;
  if (leadingWs === 0) return null;
  const monthWord = (trimmed.match(/^[a-zA-Z]+/) ?? [""])[0];
  if (monthWord === "") return null;
  const m = monthNum(monthWord);
  if (m == null) return null;
  const afterMonth = trimmed.slice(monthWord.length);
  const afterMonthTrim = afterMonth.replace(/^\s+/, "");
  const ws2 = afterMonth.length - afterMonthTrim.length;
  if (ws2 === 0) return null;
  const yearStr = (afterMonthTrim.match(/^\d+/) ?? [""])[0];
  if (yearStr.length !== 4) return null;
  const iso = validIso(Number(yearStr), m, Number(dayStr));
  const consumed = idxAfterDay + leadingWs + monthWord.length + ws2 + yearStr.length;
  return iso ? [iso, consumed] : null;
}

/** "September 1, 2026" / "Sep 1 2026". */
function tryMonthDayYear(text: string, at: number): [string, number] | null {
  const s = text.slice(at);
  const monthWord = (s.match(/^[a-zA-Z]+/) ?? [""])[0];
  if (monthWord === "") return null;
  const m = monthNum(monthWord);
  if (m == null) return null;
  const afterMonth = s.slice(monthWord.length);
  const afterMonthTrim = afterMonth.replace(/^\s+/, "");
  const ws1 = afterMonth.length - afterMonthTrim.length;
  if (ws1 === 0) return null;
  const dayStr = (afterMonthTrim.match(/^\d+/) ?? [""])[0];
  if (dayStr === "" || dayStr.length > 2) return null;
  let afterDay = afterMonthTrim.slice(dayStr.length);
  if (afterDay.startsWith(",")) afterDay = afterDay.slice(1);
  const afterDayTrim = afterDay.replace(/^\s+/, "");
  const ws2 = afterDay.length - afterDayTrim.length;
  if (ws2 === 0 && !s.slice(monthWord.length).includes(",")) return null;
  const yearStr = (afterDayTrim.match(/^\d+/) ?? [""])[0];
  if (yearStr.length !== 4) return null;
  const iso = validIso(Number(yearStr), m, Number(dayStr));
  const consumed = s.length - afterDayTrim.slice(yearStr.length).length;
  return iso ? [iso, consumed] : null;
}

function findDates(text: string): DateHit[] {
  const hits: DateHit[] = [];
  let i = 0;
  const n = text.length;
  while (i < n) {
    const c = text[i];
    if (isDigit(c)) {
      const r = tryIso(text, i) ?? tryDmySlashes(text, i) ?? tryDayMonthYear(text, i);
      if (r) { hits.push({ iso: r[0], start: i, end: i + r[1] }); i += r[1]; continue; }
    } else if (isAlpha(c)) {
      const r = tryMonthDayYear(text, i);
      if (r) { hits.push({ iso: r[0], start: i, end: i + r[1] }); i += r[1]; continue; }
    }
    i += 1;
  }
  return hits;
}

// 🔴 CLAUSE BOUNDARY — the fix for a real defect found 2026-08-30.
// The window after a date used to run a flat 40 chars, straight through any conjunction, so in
//   "the tenancy commences on 1 MARCH 2026 and the fixed term expires on 28 February 2027"
// the NEXT clause's verb ("expires") governed the FIRST date, and a tenancy START was rendered,
// exported and — worst — fed to the model as a headline fact: "This lease expires on 1 Mar 2026".
// An app whose whole promise is "cited, never guesses" was confidently stating the opposite of
// what the document said. Cut the window at the first clause break so a neighbouring verb cannot
// reach across it. Applied to BOTH windows: the same leak runs backwards too.
const CLAUSE_BREAKS = [" and ", " but ", " or ", " while ", " whereas ", " provided ", ";", ",", ".", "\n"];
function clipAfter(s: string): string {
  let cut = s.length;
  for (const b of CLAUSE_BREAKS) { const i = s.indexOf(b); if (i >= 0 && i < cut) cut = i; }
  return s.slice(0, cut);
}
function clipBefore(s: string): string {
  let cut = 0;
  for (const b of CLAUSE_BREAKS) { const i = s.lastIndexOf(b); if (i >= 0 && i + b.length > cut) cut = i + b.length; }
  return s.slice(cut);
}

function classify(lower: string, start: number, end: number): string {
  const winBefore = clipBefore(lower.slice(Math.max(start - 60, 0), start));
  const winAfter = clipAfter(lower.slice(Math.min(end, lower.length), Math.min(end + 40, lower.length)));
  const has = (hay: string, cues: string[]) => cues.some((c) => hay.includes(c));
  // "start" sits before "due" so a commencement date lands somewhere TRUE rather than falling
  // through to the generic fallback. It is checked after renewal/expiry/notice, which are the
  // stronger, more specific signals when a sentence carries both.
  for (const [label, cues] of [["renewal", RENEW_CUES], ["expiry", EXPIRE_CUES], ["notice", NOTICE_CUES], ["start", START_CUES], ["due", DUE_CUES]] as [string, string[]][]) {
    if (has(winBefore, cues) || has(winAfter, cues)) return label;
  }
  return "due";
}

function contextWindow(text: string, start: number, end: number): string {
  const from = Math.max(start - 50, 0);
  const to = Math.min(end + 50, text.length);
  const collapsed = text.slice(from, to).trim().split(/\s+/).join(" ");
  if ([...collapsed].length > 90) {
    const cut = [...collapsed].slice(0, 90).join("");
    return `…${cut.trim()}…`;
  }
  return collapsed;
}

export type DerivedDate = [label: string, iso: string, context: string];

/** ledger.rs derive_key_dates — deterministic, capped at 64, de-duped on (label, date). */
export function deriveKeyDates(text: string): DerivedDate[] {
  const MAX_DATES = 64;
  const lower = text.toLowerCase();
  const out: DerivedDate[] = [];
  const seen = new Set<string>();
  for (const h of findDates(text)) {
    if (out.length >= MAX_DATES) break;
    const label = classify(lower, h.start, h.end);
    const key = `${label}|${h.iso}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push([label, h.iso, contextWindow(text, h.start, h.end)]);
  }
  return out;
}

// ── signals.rs port: pre-ranked headline facts ─────────────────────────────────
const MONEY_CUES: [string, string][] = [
  ["deposit", "deposit"], ["rent", "rent"], ["premium", "premium"], ["excess", "excess"],
  ["deductible", "excess"], ["monthly payment", "monthly payment"], ["instalment", "instalment"],
  ["installment", "instalment"], ["fee", "fee"], ["charge", "charge"], ["penalty", "penalty"],
  ["fine", "fine"], ["coverage", "cover amount"], ["cover", "cover amount"],
  ["sum insured", "sum insured"], ["limit", "limit"], ["balance", "balance"], ["total", "total"],
  ["price", "price"], ["cost", "cost"], ["salary", "salary"],
];
const PERIOD_CUES: [string, string][] = [
  ["notice", "notice period"], ["cooling-off", "cooling-off period"], ["cooling off", "cooling-off period"],
  ["cancellation period", "cancellation period"], ["term of", "term"], ["fixed term", "fixed term"],
  ["warranty period", "warranty period"], ["guarantee", "guarantee period"], ["grace period", "grace period"],
];
const PERCENT_CUES: [string, string][] = [
  ["apr", "APR"], ["representative apr", "representative APR"], ["interest rate", "interest rate"],
  ["interest", "interest rate"], ["annual percentage rate", "APR"], ["rate of", "rate"],
  ["excess", "excess"], ["commission", "commission"], ["discount", "discount"],
  ["increase", "increase"], ["escalation", "escalation"], ["fee", "fee"],
];

export interface WebHeadlineFact { text: string; tone: string; rank: number }

const subjectPhrase = (kind: string) =>
  kind === "lease" ? "This lease" : kind === "insurance" ? "This policy" : kind === "warranty" ? "This warranty" : kind === "utility" ? "This agreement" : "This document";
const labelVerb = (label: string) =>
  label === "renewal" ? "renews" : label === "expiry" ? "expires" : label === "notice" ? "requires notice by" : label === "due" ? "has a payment due" : "has a key date";

function phraseDays(days: number): string {
  if (days === 1) return "1 day";
  if (days < 14) return `${days} days`;
  if (days < 60) return `${Math.floor((days + 3) / 7)} weeks`;
  return `${Math.floor((days + 15) / 30)} months`;
}
const isoToDays = (iso: string): number | null => {
  const d = parseIsoDay(iso);
  return d != null ? Math.floor(Date.parse(`${d}T00:00:00Z`) / 86400000) : null;
};
const parseIsoDay = (iso: string) => (/^\d{4}-\d{2}-\d{2}/.test(iso) ? iso.slice(0, 10) : null);
const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function prettyDate(iso: string): string {
  const d = parseIsoDay(iso);
  if (d == null) return iso;
  return `${Number(d.slice(8, 10))} ${MONTH_ABBR[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}`;
}

function nearestCue(text: string, start: number, cues: [string, string][]): string | null {
  const win = text.slice(Math.max(start - 80, 0), start).toLowerCase();
  const after = text.slice(start, Math.min(start + 40, text.length)).toLowerCase();
  let best: [number, string] | null = null;
  for (const [cue, label] of cues) {
    const pos = win.lastIndexOf(cue);
    if (pos >= 0) {
      const dist = win.length - pos;
      if (best == null || dist < best[0]) best = [dist, label];
    } else {
      const apos = after.indexOf(cue);
      if (apos >= 0) {
        const dist = 90 + apos;
        if (best == null || dist < best[0]) best = [dist, label];
      }
    }
  }
  return best ? best[1] : null;
}

interface FigureHit { start: number; value: number; display: string }

function readNumber(s: string): [string, number, number] | null {
  let consumed = 0, raw = "", digits = "", seenDigit = false;
  for (const ch of s) {
    if (isDigit(ch)) { raw += ch; digits += ch; consumed += 1; seenDigit = true; }
    else if (ch === "," && seenDigit) { raw += ch; consumed += 1; }
    else if (ch === "." && seenDigit) { raw += ch; digits += ch; consumed += 1; }
    else break;
  }
  while (raw.endsWith(".")) { raw = raw.slice(0, -1); digits = digits.slice(0, -1); consumed -= 1; }
  if (!seenDigit) return null;
  const value = Number(digits);
  return [raw, consumed, Number.isFinite(value) ? value : 0];
}

function findMoney(text: string): FigureHit[] {
  const hits: FigureHit[] = [];
  let i = 0;
  const n = text.length;
  outer: while (i < n) {
    const sym = text[i];
    if (sym === "£" || sym === "$" || sym === "€") {
      const r = readNumber(text.slice(i + 1));
      if (r && r[1] > 0) {
        hits.push({ start: i, value: r[2], display: `${sym}${r[0]}` });
        i += 1 + r[1];
        continue;
      }
    }
    for (const code of ["GBP", "USD", "EUR"]) {
      if (text.slice(i, i + code.length).toUpperCase() === code) {
        const after = text.slice(i + code.length);
        const trimmed = after.replace(/^\s+/, "");
        const ws = after.length - trimmed.length;
        if (ws >= 1) {
          const r = readNumber(trimmed);
          if (r && r[1] > 0) {
            hits.push({ start: i, value: r[2], display: `${code} ${r[0]}` });
            i += code.length + ws + r[1];
            continue outer;
          }
        }
      }
    }
    i += 1;
  }
  return hits;
}

function wordSpans(s: string): [number, string][] {
  const out: [number, string][] = [];
  const re = /[a-z0-9][a-z0-9']*/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) != null) out.push([m.index, m[0]]);
  return out;
}
function parseCount(tok: string): number | null {
  if (/^\d+(\.\d+)?$/.test(tok)) {
    const v = Number(tok);
    return v > 0 && v <= 120 ? v : null;
  }
  const map: Record<string, number> = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, twelve: 12, fourteen: 14, thirty: 30, sixty: 60, ninety: 90 };
  return map[tok] ?? null;
}
const countDisplay = (tok: string, value: number) =>
  isDigit(tok[0]) ? (Number.isInteger(value) ? String(value) : tok) : tok;

function findPeriods(text: string): FigureHit[] {
  const lower = text.toLowerCase();
  const words = wordSpans(lower);
  const hits: FigureHit[] = [];
  for (let w = 0; w + 1 < words.length; w++) {
    const [start, tok] = words[w];
    const value = parseCount(tok);
    if (value == null) continue;
    const unit = words[w + 1][1].replace(/'+$/, "").replace(/s$/, "");
    if (["day", "week", "month", "year"].includes(unit)) {
      const plural = value === 1 ? unit : `${unit}s`;
      hits.push({ start, value, display: `${countDisplay(tok, value)} ${plural}` });
    }
  }
  return hits;
}

function findPercents(text: string): FigureHit[] {
  const hits: FigureHit[] = [];
  let i = 0;
  const n = text.length;
  while (i < n) {
    const c = text[i];
    const prevIsNumeric = i > 0 && (isDigit(text[i - 1]) || text[i - 1] === "." || text[i - 1] === ",");
    if (isDigit(c) && !prevIsNumeric) {
      const r = readNumber(text.slice(i));
      if (r && r[1] > 0) {
        const after = text.slice(i + r[1]);
        const trimmed = after.replace(/^\s+/, "");
        const ws = after.length - trimmed.length;
        const isPct = trimmed.startsWith("%");
        const isWord = trimmed.toLowerCase().startsWith("percent");
        if ((isPct && ws <= 1) || (isWord && ws >= 1)) {
          hits.push({ start: i, value: r[2], display: `${r[0]}%` });
          i += Math.max(r[1] + ws + (isPct ? 1 : 7), r[1]);
          continue;
        }
        i += r[1];
        continue;
      }
    }
    i += 1;
  }
  return hits;
}

/** signals.rs derive_headline_facts — pre-ranked, capped at 8, de-duped on sentence. */
export function deriveHeadlineFacts(text: string, kind: string, dates: DerivedDate[], today: string): WebHeadlineFact[] {
  const MAX_FACTS = 8;
  const facts: WebHeadlineFact[] = [];
  const seen = new Set<string>();
  const push = (t: string, tone: string, rank: number) => {
    if (!seen.has(t)) { seen.add(t); facts.push({ text: t, tone, rank }); }
  };
  const subject = subjectPhrase(kind);

  const todayDays = isoToDays(today);
  for (const [label, iso] of dates) {
    const d = isoToDays(iso);
    const rel = todayDays != null && d != null ? d - todayDays : null;
    let when: string, tone: string, urgencyRank: number;
    if (rel == null) { when = ""; tone = "neutral"; urgencyRank = 40; }
    else if (rel < 0) { when = `${phraseDays(-rel)} ago`; tone = "neutral"; urgencyRank = 0; }
    else if (rel === 0) { when = "today"; tone = "urgent"; urgencyRank = 1000; }
    else if (rel <= 30) { when = `in ${phraseDays(rel)}`; tone = "urgent"; urgencyRank = 900 - rel; }
    else if (rel <= 90) { when = `in ${phraseDays(rel)}`; tone = "notable"; urgencyRank = 600 - rel; }
    else { when = `in ${phraseDays(rel)}`; tone = "neutral"; urgencyRank = Math.max(300 - Math.min(rel, 300), 50); }
    const sentence = when === ""
      ? `${subject} ${labelVerb(label)} on ${prettyDate(iso)}.`
      : `${subject} ${labelVerb(label)} on ${prettyDate(iso)} (${when}).`;
    push(sentence, tone, 4000 + urgencyRank);
  }
  for (const hit of findMoney(text)) {
    const label = nearestCue(text, hit.start, MONEY_CUES);
    if (label != null) {
      const magnitude = Math.trunc(Math.max(Math.log10(hit.value), 0) * 100);
      push(`${subject} states a ${label} of ${hit.display}.`, magnitude >= 350 ? "notable" : "neutral", 2000 + magnitude);
    }
  }
  for (const hit of findPeriods(text)) {
    const label = nearestCue(text, hit.start, PERIOD_CUES);
    if (label != null) {
      const notable = label.includes("notice") || label.includes("cooling");
      push(`${subject} states a ${label} of ${hit.display}.`, notable ? "notable" : "neutral", notable ? 1400 : 1000);
    }
  }
  for (const hit of findPercents(text)) {
    const label = nearestCue(text, hit.start, PERCENT_CUES);
    if (label != null) {
      const actionRelevant = label.includes("APR") || label.includes("interest");
      const article = label.startsWith("APR") || label.startsWith("representative APR") ? "an" : "a";
      push(`${subject} states ${article} ${label} of ${hit.display}.`, actionRelevant ? "notable" : "neutral", actionRelevant ? 1300 : 900);
    }
  }
  facts.sort((a, b) => b.rank - a.rank);
  facts.length = Math.min(facts.length, MAX_FACTS);
  return facts;
}

// ── db.rs port: chunker + tokenizer + passage search ───────────────────────────
const CHUNK_CHARS = 1000;

/** db.rs chunk_text — ~1000-char passages, whitespace-snapped, page = floor(idx/3)+1. */
export function chunkText(text: string): [number, string][] {
  const trimmed = text.trim();
  if (trimmed === "") return [];
  const chars = [...trimmed];
  const chunks: [number, string][] = [];
  let i = 0, chunkIndex = 0;
  while (i < chars.length) {
    let end = Math.min(i + CHUNK_CHARS, chars.length);
    if (end < chars.length) {
      const floor = Math.max(i + 1, end - 120);
      for (let k = end - 1; k >= floor; k--) {
        if (/\s/.test(chars[k])) { end = k + 1; break; }
      }
    }
    const chunk = chars.slice(i, end).join("").trim();
    if (chunk !== "") {
      chunks.push([Math.floor(chunkIndex / 3) + 1, chunk]);
      chunkIndex += 1;
    }
    i = end;
  }
  return chunks;
}

const STOPWORDS = new Set([
  "where", "what", "which", "when", "who", "whom", "whose", "how", "why",
  "the", "and", "for", "from", "with", "that", "this", "these", "those",
  "did", "does", "was", "were", "are", "have", "has", "had", "get", "got",
  "say", "says", "said", "tell", "show", "find", "give", "about", "any", "all", "some",
  "can", "could", "would", "should", "must", "may", "might", "will",
  "document", "documents", "doc", "docs", "file", "files",
  "last", "next", "ago", "recent", "recently",
  "today", "yesterday", "tomorrow", "week", "weeks", "month", "months",
  "year", "years", "day", "days",
]);

/** db.rs fts_tokens — lowercased prefix tokens with stop/temporal words dropped. */
export function ftsTokens(raw: string): string[] {
  return raw
    .split(/[^a-zA-Z0-9_]+/)
    .filter(Boolean)
    .map((t) => t.toLowerCase())
    .filter((t) => t.length > 2 && !STOPWORDS.has(t));
}

export interface WebPassage { doc_id: string; title: string; kind: string; page: number; snippet: string }
export interface DocForSearch { id: string; title: string; kind: string; body: string }

/** db.rs search_passages — AND-of-prefixes first (precise), then OR (recall). The browser has
 *  no FTS5, so passages are chunked on the fly and ranked by prefix-match count (desc). */
export function searchPassages(docs: DocForSearch[], query: string, limit: number): WebPassage[] {
  const tokens = ftsTokens(query);
  if (tokens.length === 0) return [];
  const cap = Math.min(Math.max(limit, 1), 24);
  interface Scored { p: WebPassage; score: number }
  const all: { doc: DocForSearch; page: number; chunk: string; words: string[] }[] = [];
  for (const doc of docs) {
    for (const [page, chunk] of chunkText(doc.body)) {
      all.push({ doc, page, chunk, words: chunk.toLowerCase().split(/[^a-z0-9_]+/).filter(Boolean) });
    }
  }
  const matchCount = (words: string[], token: string) => words.filter((w) => w.startsWith(token)).length;
  for (const requireAll of [true, false]) {
    const scored: Scored[] = [];
    for (const { doc, page, chunk, words } of all) {
      const perToken = tokens.map((t) => matchCount(words, t));
      const hitTokens = perToken.filter((c) => c > 0).length;
      if (requireAll ? hitTokens === tokens.length : hitTokens > 0) {
        scored.push({
          p: { doc_id: doc.id, title: doc.title, kind: doc.kind, page, snippet: chunk },
          score: perToken.reduce((a, b) => a + b, 0),
        });
      }
    }
    if (scored.length) {
      scored.sort((a, b) => b.score - a.score);
      return scored.slice(0, cap).map((x) => x.p);
    }
  }
  return [];
}

// ── db.rs port: kind normalisation (add_document parity) ───────────────────────
const DOC_KINDS = ["lease", "insurance", "warranty", "id", "utility", "other"];
export const normalizeKind = (raw: string) => {
  const k = raw.trim().toLowerCase();
  return DOC_KINDS.includes(k) ? k : "other";
};
